import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useContractCloudFunctions } from "./useContractCloudFunctions";
import { generateContractPdf, blobToFile } from "@/utils/pdfUtils";
import { uploadApplicationDocument } from "@/services/storageService";
import { useApplication } from "@/contexts/ApplicationContext";
import { useActiveJob } from "@/contexts/ActiveJobContext";
import * as Sentry from "@sentry/react";

export const useContractSubmissionHandlers = (applicationId: string) => {
  const { job } = useActiveJob();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { submitDriverContract } = useContractCloudFunctions();
  const { refetch } = useApplication();
  const { toast } = useToast();
  const navigate = useNavigate();

  /**
   * Handle contract submission with signature
   */
  const handleContractSubmit = async (
    signatureDataUrl: string,
    firstName: string,
    lastName: string,
    driverNumber: string = "",
    companyName: string = "",
    address: string = "",
    vatNumber: string = "",
    salary: string = ""
  ) => {
    if (!applicationId) {
      toast({
        title: "Error",
        description: "Application ID is required",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // 1. Upload signature image to storage
      const signatureBlob = await fetch(signatureDataUrl).then((res) =>
        res.blob()
      );
      const signatureFile = blobToFile(
        signatureBlob,
        `signature-${applicationId}-${Date.now()}.png`,
        "image/png"
      );

      const signatureResult = await uploadApplicationDocument(
        signatureFile,
        applicationId,
        "contract-signature"
      );

      if (!signatureResult.success) {
        toast({
          title: "Signature Upload Error",
          description: signatureResult.message || "Failed to upload signature",
          variant: "destructive",
        });
        return;
      }

      const signatureUrl = signatureResult.url;
      // 2. Generate combined contract PDF with signature
      const currentStage = job.stages.find((stage) => stage.id === "contract");
      const contractText =
        (currentStage?.meta?.contract as string) ||
        (job.contract as string) ||
        "";

      if (!contractText) {
        Sentry.captureException("Contract text is missing", {
          extra: {
            context: "useContractSubmissionHandlers.handleContractSubmit",
            applicationId,
            job,
            currentStage,
            contractText,
            signatureUrl,
          },
        });
        toast({
          title: "Contract Text Error",
          description: "Contract text is missing",
          variant: "destructive",
        });
        return;
      }
      const applicantName = `${firstName} ${lastName}`;

      const contractPdfBlob = await generateContractPdf(
        contractText,
        signatureUrl,
        applicantName,
        driverNumber,
        companyName,
        address,
        vatNumber,
        salary
      );
      const contractPdfFile = blobToFile(
        contractPdfBlob,
        `contract-${applicationId}-${Date.now()}.pdf`,
        "application/pdf"
      );

      // 3. Upload contract PDF to storage
      const contractPdfResult = await uploadApplicationDocument(
        contractPdfFile,
        applicationId,
        "driver-contract"
      );

      if (!contractPdfResult.success) {
        toast({
          title: "Contract PDF Upload Error",
          description:
            contractPdfResult.message || "Failed to upload contract PDF",
          variant: "destructive",
        });
        return;
      }

      const contractPdfUrl = contractPdfResult.url;

      // 4. Submit to cloud function
      const response = await submitDriverContract.mutateAsync({
        applicationId,
        signatureUrl,
        contractPdfUrl,
      });

      // 5. Show success message
      toast({
        title: "Contract Submitted",
        description: "Your contract has been successfully submitted",
        variant: "default",
      });
      await refetch();
      // 6. Navigate to the next page based on nextStage
      if (response.data?.nextStage) {
        navigate(`/${response.data.nextStage}`);
      } else {
        // Default to completed page if no nextStage or if nextStage is "complete"
        navigate("/complete");
      }
    } catch (error) {
      console.error("Error submitting contract:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to submit contract",
        variant: "destructive",
      });
    }
  };

  return {
    handleContractSubmit,
    isSubmitting,
    isContractSubmitError: submitDriverContract.isError,
    contractSubmitError: submitDriverContract.error,
  };
};
